import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { CheckCircle, CalendarCheck, MessageSquare } from 'lucide-react';
import { getDebateTarget, LIVE_WINDOW } from '../utils/debateTime';

// ── Countdown helpers ──────────────────────────────────────────────────────────

// Debate timing lives in utils/debateTime.js so every view agrees.

function pad(n) { return String(n).padStart(2, '0'); }

function formatCountdown(ms) {
  if (ms <= 0) return null;
  const s = Math.floor(ms / 1000);
  const days = Math.floor(s / 86400);
  const hrs = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const secs = s % 60;
  if (days > 0) return `${days}d ${pad(hrs)}:${pad(mins)}:${pad(secs)}`;
  return `${pad(hrs)}:${pad(mins)}:${pad(secs)}`;
}

// ── Countdown strip styles ─────────────────────────────────────────────────────

const tickPulse = keyframes`
  0%   { opacity: 1; }
  50%  { opacity: 0.7; }
  100% { opacity: 1; }
`;

const CountdownStrip = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border-radius: 8px;
  padding: 7px 12px;
  margin: 10px 0 4px;
  gap: 8px;
  cursor: default;
`;

const CountdownLabel = styled.span`
  font-size: 0.7em;
  color: rgba(255,255,255,0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  white-space: nowrap;
`;

const CountdownValue = styled.span`
  font-size: 0.9em;
  font-weight: 700;
  color: ${p => p.urgent ? '#f56565' : '#a78bfa'};
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.5px;
  animation: ${tickPulse} 1s ease-in-out infinite;
`;

const CountdownDate = styled.span`
  font-size: 0.68em;
  color: rgba(255,255,255,0.35);
  white-space: nowrap;
`;

const Card = styled(Link)`
  display: block;
  text-decoration: none;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 16px;
  padding: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #1a202c;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  position: relative;

  /* The CLOSED badge is absolutely positioned, and the department tag is what
     normally pushes the title clear of it. Parliament returns no department
     for some petitions (e.g. 757233), and there the title ran underneath the
     badge. Reserve the space on the title itself instead of depending on a
     tag that may not exist. */
  ${props => props.status === 'closed' && `
    &::before {
      content: 'CLOSED';
      position: absolute;
      top: 12px;
      right: 12px;
      background: #ef4444;
      color: white;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 0.7em;
      font-weight: 700;
      letter-spacing: 0.5px;
      z-index: 1;
    }

    .petition-title { padding-right: 82px; }
    /* A department tag already clears the badge, so the title does not need
       to reserve the space twice. */
    .petition-department ~ .petition-title { padding-right: 0; }
  `}

  &:hover {
    background: rgba(255, 255, 255, 1);
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
  }

  .petition-title {
    font-weight: 600;
    font-size: 1.05em;
    margin-bottom: 8px;
    line-height: 1.4;
    color: #2d3748;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    min-height: 48px;
  }

  .petition-id {
    font-size: 0.8em;
    color: #718096;
    font-weight: 500;
    margin-bottom: 12px;
    font-family: monospace;
    background: #f7fafc;
    padding: 4px 8px;
    border-radius: 4px;
    display: inline-block;
  }

  .petition-department {
    background: linear-gradient(135deg, #1a202c 0%, #2d3748 100%);
    color: white;
    padding: 5px 12px;
    border-radius: 12px;
    font-size: 0.75em;
    font-weight: 600;
    margin-bottom: 12px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    letter-spacing: 0.3px;
    position: relative;
    transition: all 0.2s ease;
    border: 1px solid rgba(255, 255, 255, 0.1);
    
    &:hover {
      transform: scale(1.05);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
      background: linear-gradient(135deg, #2d3748 0%, #4a5568 100%);
    }
    
    .dept-icon {
      font-size: 0.9em;
    }
    
    .dept-tooltip {
      position: absolute;
      bottom: 100%;
      left: 50%;
      transform: translateX(-50%);
      background: #1a202c;
      color: white;
      padding: 6px 10px;
      border-radius: 6px;
      font-size: 0.9em;
      white-space: nowrap;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.2s ease;
      margin-bottom: 4px;
      
      &::after {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        border: 4px solid transparent;
        border-top-color: #1a202c;
      }
    }
    
    &:hover .dept-tooltip {
      opacity: 1;
    }
  }

  .petition-stats {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e2e8f0;
  }

  .signature-count {
    font-size: 1.3em;
    font-weight: 700;
    color: ${props => props.status === 'closed' ? '#ef4444' : '#5b21b6'};
    display: flex;
    align-items: baseline;
    gap: 4px;
  }

  .signature-label {
    font-size: 0.6em;
    font-weight: 400;
    color: #718096;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .created-at {
    font-size: 0.75em;
    color: #718096;
    margin-top: 4px;
  }

  .badges {
    display: flex;
    flex-direction: column;
    gap: 6px;
    align-items: flex-end;
  }

  .badge {
    padding: 3px 7px;
    border-radius: 6px;
    font-size: 0.65em;
    font-weight: 600;
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 4px;
    letter-spacing: 0.2px;

    svg {
      width: 10px;
      height: 10px;
      flex-shrink: 0;
    }
  }

  .badge.response {
    background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
    color: #15803d;
    border: 1px solid #86efac;
  }

  .badge.debate-completed {
    background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
    color: #92400e;
    border: 1px solid #fcd34d;
  }

  .badge.debate-scheduled {
    background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
    color: #1d4ed8;
    border: 1px solid #93c5fd;
    animation: gentle-pulse 2s infinite;
  }

  .badge.debate-live {
    background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
    color: #dc2626;
    border: 1px solid #f87171;
    animation: gentle-pulse 1s infinite;
  }

  @keyframes gentle-pulse {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.7; }
  }
`;

// Department mapping with full names and icons (removed individual colors)
const DEPARTMENT_INFO = {
  'HO': {
    name: 'Home Office',
    shortName: 'Home Office',
    icon: '',
    description: 'Immigration, Security, Police'
  },
  'DfT': {
    name: 'Transport',
    shortName: 'Transport',
    icon: '',
    description: 'Roads, Rail, Aviation'
  },
  'DHSC': {
    name: 'Health & Social Care',
    shortName: 'Health',
    icon: '',
    description: 'NHS, Public Health, Social Care'
  },
  'DfE': {
    name: 'Education',
    shortName: 'Education',
    icon: '',
    description: 'Schools, Universities, Skills'
  },
  'HMT': {
    name: 'Treasury',
    shortName: 'Treasury',
    icon: '',
    description: 'Economy, Tax, Public Spending'
  },
  'FCDO': {
    name: 'Foreign & Commonwealth',
    shortName: 'Foreign Office',
    icon: '',
    description: 'International Relations, Diplomacy'
  },
  'BEIS': {
    name: 'Business & Trade',
    shortName: 'Business',
    icon: '',
    description: 'Business, Energy, Industrial Strategy'
  },
  'DWP': {
    name: 'Work & Pensions',
    shortName: 'Work & Pensions',
    icon: '',
    description: 'Benefits, Pensions, Employment'
  },
  'MoJ': {
    name: 'Justice',
    shortName: 'Justice',
    icon: '',
    description: 'Courts, Prisons, Probation'
  },
  'MoD': {
    name: 'Defence',
    shortName: 'Defence',
    icon: '',
    description: 'Armed Forces, Defence Policy'
  },
  'DEFRA': {
    name: 'Environment & Rural',
    shortName: 'Environment',
    icon: '',
    description: 'Environment, Food, Rural Affairs'
  },
  'DCMS': {
    name: 'Culture & Sport',
    shortName: 'Culture',
    icon: '',
    description: 'Culture, Media, Sport, Digital'
  },
  'DLUHC': {
    name: 'Housing & Communities',
    shortName: 'Housing',
    icon: '',
    description: 'Housing, Local Government, Communities'
  },
  'CO': {
    name: 'Cabinet Office',
    shortName: 'Cabinet Office',
    icon: '',
    description: 'Government Operations, Civil Service'
  },
  'DESNZ': {
    name: 'Energy & Net Zero',
    shortName: 'Energy',
    icon: '',
    description: 'Energy Security, Net Zero'
  },
  'DBT': {
    name: 'Business & Trade',
    shortName: 'Trade',
    icon: '',
    description: 'International Trade, Business'
  },
  'DSIT': {
    name: 'Science & Technology',
    shortName: 'Science & Tech',
    icon: '',
    description: 'Science, Innovation, Technology'
  }
};


const CardCountdown = ({ dateStr }) => {
  const [diff, setDiff] = useState(() => {
    const t = getDebateTarget(dateStr);
    return t ? t - Date.now() : null;
  });

  useEffect(() => {
    const target = getDebateTarget(dateStr);
    if (!target) return;
    const id = setInterval(() => setDiff(target - Date.now()), 1000);
    return () => clearInterval(id);
  }, [dateStr]);

  if (!diff) return null;

  if (diff <= 0 && diff > -LIVE_WINDOW) {
    return (
      <CountdownStrip onClick={e => e.stopPropagation()}>
        <CountdownLabel>Debate</CountdownLabel>
        <CountdownValue urgent style={{ color: '#f87171' }}>🔴 Live Now</CountdownValue>
        <CountdownDate>In progress</CountdownDate>
      </CountdownStrip>
    );
  }
  if (diff <= -LIVE_WINDOW) return null;

  const display = formatCountdown(diff);
  if (!display) return null;

  const isUrgent = diff < 2 * 86400 * 1000;
  const target = getDebateTarget(dateStr);
  const dateLabel = target.toLocaleDateString('en-GB', {
    timeZone: 'Europe/London', day: 'numeric', month: 'short',
  }) + ' · 4:30 PM';

  return (
    <CountdownStrip onClick={e => e.stopPropagation()}>
      <CountdownLabel>Debate in</CountdownLabel>
      <CountdownValue urgent={isUrgent}>{display}</CountdownValue>
      <CountdownDate>{dateLabel}</CountdownDate>
    </CountdownStrip>
  );
};

const PetitionCard = ({ petition, to, showCountdown = false }) => {
  const formatSignatures = (count) => {
    return count.toLocaleString();
  };

  // Calendar days in UK time, not a rolling 24-hour window. Petitions are a
  // UK Parliament service, so "today" has to mean today in London regardless
  // of where the reader is.
  const ukDayNumber = (date) => {
    const [d, m, y] = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London', day: '2-digit', month: '2-digit', year: 'numeric',
    }).format(date).split('/');
    return Math.floor(Date.UTC(+y, +m - 1, +d) / 86400000);
  };

  const getDaysAgo = (dateString) => {
    const createdDate = new Date(dateString);
    if (Number.isNaN(createdDate.getTime())) return '';
    const diffDays = Math.max(0, ukDayNumber(new Date()) - ukDayNumber(createdDate));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays} days ago`;
  };

  // Get department info
  const getDepartmentInfo = () => {
    const acronym = petition.departmentAcronym;
    if (!acronym || acronym === 'N/A') return null;

    // Check if we have info for this department
    const deptInfo = DEPARTMENT_INFO[acronym];
    if (deptInfo) return { ...deptInfo, acronym };

    // Fallback for unknown departments
    return {
      name: acronym,
      shortName: acronym,
      icon: '',
      description: 'Government Department',
      acronym
    };
  };

  // Enhanced debate status logic
  const getDebateStatus = () => {
    const now = new Date();

    // Check if there's a scheduled debate date
    if (petition.scheduledDebateDate) {
      const debateDate = getDebateTarget(petition.scheduledDebateDate);
      if (debateDate > now) {
        return {
          type: 'scheduled',
          icon: '',
          text: 'Debate Scheduled',
          date: debateDate
        };
      }
      // Within 3-hour live window
      if (now - debateDate < LIVE_WINDOW) {
        return {
          type: 'live',
          icon: '🔴',
          text: 'Live Now',
          date: debateDate
        };
      }
    }

    // Check if debate has already happened
    if (petition.debateOutcomeAt || petition.hasDebate) {
      return {
        type: 'completed',
        icon: '',
        text: 'Debated',
        date: petition.debateOutcomeAt ? new Date(petition.debateOutcomeAt) : null
      };
    }

    return null;
  };

  const debateStatus = getDebateStatus();
  const deptInfo = getDepartmentInfo();

  return (
    <Card
      to={to}
      status={petition.state || petition.status}
    >
      {deptInfo && (
        <div className="petition-department">
          <span className="dept-icon">{deptInfo.icon}</span>
          <span>{deptInfo.shortName}</span>
          <div className="dept-tooltip">
            {deptInfo.name}
            {deptInfo.description && ` - ${deptInfo.description}`}
          </div>
        </div>
      )}

      <div className="petition-title">
        {petition.title}
      </div>

      {showCountdown && petition.scheduledDebateDate && (
        <CardCountdown dateStr={petition.scheduledDebateDate} />
      )}

      <div className="petition-stats">
        <div>
          <div className="signature-count">
            {formatSignatures(petition.signatureCount)}
          </div>
          <div className="signature-label">signatures</div>
          <div className="created-at">
            {petition.state === 'rejected' || petition.status === 'rejected' ?
              `Rejected ${getDaysAgo(petition.rejectedAt || petition.createdAt)}` :
              petition.state === 'closed' || petition.status === 'closed' ?
                `Closed ${getDaysAgo(petition.closedAt)}` :
                `Opened ${getDaysAgo(petition.openedAt || petition.createdAt)}`
            }
          </div>
        </div>
        <div className="badges">
          {petition.hasGovernmentResponse && (
            <span className="badge response">
              <CheckCircle />
              Gov Response
            </span>
          )}
          {debateStatus && (
            <span className={`badge ${
              debateStatus.type === 'live' ? 'debate-live' :
              debateStatus.type === 'scheduled' ? 'debate-scheduled' :
              'debate-completed'
            }`}>
              {debateStatus.type === 'live' ? '🔴' :
               debateStatus.type === 'scheduled' ? <CalendarCheck /> :
               <MessageSquare />}
              {debateStatus.text}
            </span>
          )}
        </div>
      </div>
    </Card>
  );
};

export default PetitionCard;